import { forwardRef, memo, useState, useCallback, type SyntheticEvent } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import { visuallyHidden } from '@mui/utils';
import Box, { type BoxProps } from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Snackbar, { type SnackbarCloseReason } from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
// Material Icon imports
import Share from 'mdi-material-ui/Share';
// local imports
import { SNACKBAR_AUTOHIDE_DURATION, SNACKBAR_ELEVATION, anchorOrigin } from '../config/params';

type CopyToClipboardButtonProps = {
  readonly text: string;
  readonly message?: string;
  readonly srLabel?: string;
  readonly disabled?: boolean | null;
  readonly className?: string;
  // for Storybook only
  readonly testCopied?: boolean;
};

const CopyToClipboardButton = forwardRef<HTMLButtonElement, CopyToClipboardButtonProps>(({
  text,
  message,
  srLabel,
  disabled,
  className,
  testCopied = false
}, ref) => {
  const [snackbarOpen, setSnackbarOpen] = useState(testCopied);

  const handleSnackbarOpen = useCallback((_text: string, success: boolean) => {
    if (success) setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = useCallback((_event: Event | SyntheticEvent, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  }, []);

  const alert = (
    <Alert onClose={handleSnackbarClose} severity="success" variant="standard" elevation={SNACKBAR_ELEVATION}>
      <FormattedMessage id={message} defaultMessage={message}/>
    </Alert>
  );

  return (
    <>
      <CopyToClipboard text={text} onCopy={handleSnackbarOpen}>
        <IconButton ref={ref} color="primary" disabled={disabled ? true : undefined} className={className}>
          <Share color="inherit"/>
          {srLabel ? (
            <Box sx={visuallyHidden as BoxProps['sx']}>
              <FormattedMessage id={srLabel} defaultMessage={srLabel}/>
            </Box>
          ) : undefined}
        </IconButton>
      </CopyToClipboard>
      {(typeof jest === 'undefined' && (
        <Snackbar
            open={snackbarOpen}
            onClose={handleSnackbarClose}
            autoHideDuration={0.5 * SNACKBAR_AUTOHIDE_DURATION}
            anchorOrigin={anchorOrigin}
        >
          {alert}
        </Snackbar>
      )) || (snackbarOpen ? alert : undefined)}
    </>
  );
});

CopyToClipboardButton.displayName = 'CopyToClipboardButton';

export default memo(CopyToClipboardButton);
